import { all, call, put } from 'redux-saga/effects';
import moment from 'moment';

import * as types from './meeting-simple.types';
import * as actions from './meeting-simple.actions';

import { select, selectWithParams } from '../../../utils/selector.utils';
import { meetingSimpleSelectors } from './meeting-simple.selectors';

import { apiRequest } from '../../api/api.saga-services';

import * as meetingApi from '../../api/request-config/meeting.api';
import * as scoreApi from '../../api/request-config/score.api';
import * as kpiApi from '../../api/request-config/kpi.api';

import { IMeetingSimple, IMeetingSimpleResponse } from './meeting-simple.types';
import { IScore } from '../score/score.types';
import { ISession } from '../session/session.types';
import { IKpiRowScoreboard } from '../kpi-row-scoreboard/kpi-row-scoreboard.types';
import { IKpiRow } from '../kpi-row/kpi-row.types';

// Resources
import { IAgendaItem } from '../agenda-item/agenda-item.types';
import { authSelectors } from '../../auth/auth.selectors';
import * as kpiService from '../kpi/kpi.saga-service';
import * as kpiRowService from '../kpi-row/kpi-row.saga-service';
import * as kpiRowScoreboardService from '../kpi-row-scoreboard/kpi-row-scoreboard.saga-service';
import { IMeeting, IMeetingParticipantsUpdateForm } from '../meeting/meeting.types';
import * as scoreService from '../score/score.saga-service';
import * as sessionResourceService from '../session/session.saga-service';
import * as sessionService from '../session/session.saga-service';
import { sessionSelectors } from '../session/session.selectors';
import { IUser } from '../user/user.types';
import { userSelectors } from '../user/user.selectors';
import { kpiPageSelectors } from '../../page/page-storage/kpi/kpi.selectors';
import { clearNotAttended } from '../../page/page-storage/sessions/sessions.actions';
import { convertDateAndTimeToUTC } from '../../../utils/date.utils';
import { kpiRowSelectors } from '../kpi-row/kpi-row.selectors';
import { kpiSelectors } from '../kpi/kpi.selectors';
import { IKpi } from '../kpi/kpi.types';
import { meetingFolderSelectors } from '../meeting-folder/meeting-folder.selectors';
import { IMeetingFolder } from '../meeting-folder/meeting-folder.types';
import { storeMeetingFolder } from '../meeting-folder/meeting-folder.saga-service';
import { IKpiSearchParams } from '../../page/page-storage/kpi/kpi.types';
import * as meetingArchiveService from '../../page/page-storage/meeting-archive/meeting-archive.saga-service';

export function* storeMeetingSimple(meeting: types.IMeetingSimple | types.IMeetingSimple[]) {
  yield put(actions.storeMeetingSimple(meeting));
  return meeting;
}

export function* clearMeetingSimple(meetingId: types.IMeetingSimple['id']) {
  yield put(actions.clearMeetingSimple(meetingId));
}

export function* clearAllMeetingsSimple() {
  const meetingIds = (yield select(meetingSimpleSelectors.selectResourceIds)) as IMeetingSimple['id'];
  yield put(actions.clearMeetingSimple(meetingIds));
}

/* This method sends api request and handle response */
export function* searchMeetingsSimple(searchParams?: types.IMeetingSimpleSearchParams) {
  const isLoggedUserAdmin = (yield select(authSelectors.selectUserIsAdmin)) as IUser['isAdmin'];
  let meetingResponse;

  if (isLoggedUserAdmin) {
    meetingResponse = (yield call(
      apiRequest,
      meetingApi.getMeetingSimpleApiForAdmin(),
    )) as types.IMeetingSimpleResponse[];
  } else {
    meetingResponse = (yield call(
      apiRequest,
      meetingApi.getMeetingSimpleApi(searchParams),
    )) as types.IMeetingSimpleResponse[];
  }

  if (!meetingResponse) {
    return;
  }

  const meetings: IMeetingSimple[] = meetingResponse.map((meeting) => meetingResponseToMeeting(meeting));

  yield call(storeMeetingSimple, meetings);
  return meetings;
}

/**
 * Transforms/Converts the provided start date and time into UTC using the given timezone.
 * For internal use.
 * @param startDate Local start date of the meeting
 * @param startTime Local start time of the meeting
 * @param timezone IANA timezone identifier to use for the conversion
 * @returns The converted start date and time in UTC
 */
export const convertMeetingDatesToUTCBeforeApi = (startDate: string, startTime: string, timezone: string) => {
  const { date, time } = convertDateAndTimeToUTC(moment(startDate).format('YYYY-MM-DD'), startTime, timezone);

  return {
    startDate: date,
    startTime: time,
  };
};

/* This method sends api request and handle response */
export function* createMeetingSimple(meeting: types.IMeetingSimpleCreate) {
  // convert dates
  const loggedUserId = (yield select(authSelectors.selectLoggedUser)) as IUser['id'];
  const loggedUser = (yield selectWithParams(userSelectors.selectResourceById, loggedUserId)) as IUser;

  const { startDate, startTime } = convertMeetingDatesToUTCBeforeApi(
    meeting.startDate as string,
    meeting.startTime as string,
    loggedUser.timezone as string,
  );

  const newMeeting = { ...meeting, startDate, startTime } as types.IMeetingSimpleCreate;

  const meetingResponse = (yield call(
    apiRequest,
    meetingApi.createMeetingSimpleApi(newMeeting),
  )) as types.IMeetingSimpleResponse;
  if (!meetingResponse) {
    return;
  }
  const createdMeeting = meetingResponseToMeeting(meetingResponse);
  yield call(storeMeetingSimple, createdMeeting);
  return createdMeeting;
}

export function* updateMeetingParticipants(id: IMeetingSimple['id'], data: IMeetingParticipantsUpdateForm) {
  yield call(apiRequest, meetingApi.updateMeetingParticipantsApi(id, data));
}

// /* This method sends api request and handle response */
export function* updateMeetingSimple(id: IMeetingSimple['id'], meeting: types.IMeetingSimpleUpdate) {
  // convert dates
  const loggedUserId = (yield select(authSelectors.selectLoggedUser)) as IUser['id'];
  const loggedUser = (yield selectWithParams(userSelectors.selectResourceById, loggedUserId)) as IUser;

  let newMeeting = meeting;
  if (meeting.startTime && meeting.startDate && loggedUser.timezone) {
    const { startDate, startTime } = convertMeetingDatesToUTCBeforeApi(
      meeting.startDate as string,
      meeting.startTime as string,
      loggedUser.timezone as string,
    );

    newMeeting = { ...meeting, startDate, startTime } as types.IMeetingSimpleUpdate;
  }

  const meetingResponse = (yield call(
    apiRequest,
    meetingApi.updateMeetingSimpleApi(id, { ...newMeeting }),
  )) as types.IMeetingSimpleResponse;
  if (!meetingResponse) {
    return;
  }
  const updatedMeeting = meetingResponseToMeeting(meetingResponse);
  yield call(storeMeetingSimple, updatedMeeting);
  return updatedMeeting;
}

export function* updateMeetingFolder(id: IMeetingSimple['id'], data: any) {
  const meetingResponse = (yield call(
    apiRequest,
    meetingApi.setFolderForMeeting(id, data),
  )) as types.IMeetingSimpleResponse;
  if (!meetingResponse) {
    return;
  }

  const meetingFolders = (yield select(meetingFolderSelectors.selectResourceList)) as IMeetingFolder[];
  let oldMeetingFolder = meetingFolders.find((folder) => folder.meetingIds.includes(id));

  if (oldMeetingFolder) {
    oldMeetingFolder = {
      ...oldMeetingFolder,
      meetingIds: oldMeetingFolder.meetingIds.filter((meetingId) => meetingId !== id),
    };

    yield call(storeMeetingFolder, oldMeetingFolder);
  }

  let newMeetingFolder = (yield selectWithParams(
    meetingFolderSelectors.selectResourceById,
    data.folderId,
  )) as IMeetingFolder;

  if (newMeetingFolder) {
    newMeetingFolder = { ...newMeetingFolder, meetingIds: [...newMeetingFolder.meetingIds, id] };
    yield call(storeMeetingFolder, newMeetingFolder);
  }
}

export function* deleteMeetingSimple(meetingId: types.IMeetingSimple['id']) {
  return yield call(apiRequest, meetingApi.deleteMeetingApi(meetingId));
}

export function* startMeetingSession(meetingId: types.IMeetingSimple['id']) {
  const response = yield call(apiRequest, meetingApi.startMeetingSessionApi({ id: meetingId }));
  if (!response.session) {
    return;
  }

  const updatedSessionsIds = (yield call(
    prepareStartedSessionInStore,
    response.session,
    meetingId,
  )) as ISession['id'][];

  return { updatedSessionsIds, startedSessionId: response.session.id };
}

export function* prepareStartedSessionInStore(startedSession: ISession, meetingId: IMeeting['id']) {
  const kpi = (yield selectWithParams(kpiSelectors.selectResourceById, meetingId)) as IKpi;

  const startedSessionId = startedSession.id;
  const storedSessionsIds = kpi.sessionIds;
  const isCustomSession = !storedSessionsIds.includes(startedSessionId);

  let updatedSessionIds = [...storedSessionsIds];
  if (isCustomSession) {
    const { newKpi, newKpiRows, newKpiRowScoreboards, newSessionsIds } = yield call(
      generateCustomSession,
      startedSession,
      meetingId,
    );

    yield call(kpiService.storeKpi, newKpi);
    yield call(kpiRowService.storeKpiRow, newKpiRows);
    yield call(kpiRowScoreboardService.storeKpiRowScoreboard, newKpiRowScoreboards);

    updatedSessionIds = newSessionsIds;
  }

  yield call(sessionService.storeUpdatedSessionResource, startedSession);
  yield call(meetingArchiveService.upsertMeetingArchiveTranscriptFromSession, startedSession);
  return updatedSessionIds;
}

export function* generateCustomSession(newSession: ISession, meetingId: IMeetingSimple['id']) {
  const storedSessionsIds = (yield select(kpiPageSelectors.selectSessionIds)) as ISession['id'][];

  const newSessionId = newSession.id;

  const storedSessions = (yield selectWithParams(
    sessionSelectors.selectResourcesById,
    storedSessionsIds,
  )) as ISession[];
  const newSessionsIds = [...storedSessionsIds];

  const index = storedSessions.findIndex((session: ISession) => session.sessionDate > newSession.sessionDate);

  if (index !== -1) {
    newSessionsIds.splice(index, 0, newSessionId);
  }

  const kpiRowsIds = (yield select(kpiRowSelectors.selectResourceIds)) as IKpiRow['id'][];
  const kpiRows = (yield selectWithParams(kpiRowSelectors.selectResourcesById, kpiRowsIds)) as IKpiRow[];

  const newKpiRowScoreboards: IKpiRowScoreboard[] = [];
  const newKpiRows: IKpiRow[] = [...kpiRows];

  let newKpi = (yield selectWithParams(kpiSelectors.selectResourceById, meetingId)) as IKpi;
  newKpi = { ...newKpi, sessionIds: newSessionsIds };

  newKpiRows.forEach((kpiRow: IKpiRow) => {
    const kpiRowScoreboardIds = kpiRow.kpiRowScoreboardIds as IKpiRowScoreboard['id'][];

    const newScoreboard = {
      id: `${kpiRow.id}/${newSessionId}`,
      sessionId: newSessionId,
      scoresId: kpiRow.id,
      measured: '',
      note: '',
    };
    kpiRowScoreboardIds.splice(index, 0, newScoreboard.id);
    newKpiRowScoreboards.push(newScoreboard);
  });
  return {
    newKpiRowScoreboards,
    newKpiRows,
    newSessionsIds,
    newKpi,
  };
}

export function* finishMeetingSession(
  meetingId: types.IMeetingSimple['id'],
  sessionId: ISession['id'],
  cancelled: boolean,
) {
  yield call(apiRequest, meetingApi.finishMeetingSessionApi({ sessionId, cancelled }));
  yield put(clearNotAttended());
}

export function* joinMeeting(sessionId: ISession['id']) {
  const response = (yield call(apiRequest, meetingApi.joinMeetingApi({ sessionId }))) as {
    sessionId: ISession['id'];
  };

  if (response.sessionId) {
    const session = (yield selectWithParams(sessionSelectors.selectResourceById, sessionId)) as ISession;
    const loggedUserId = (yield select(authSelectors.selectLoggedUser)) as IUser['id'];
    const updatedMembers = (yield call(addUserToMeetingMembers, loggedUserId, session.meetingMembers)) as IUser['id'][];

    yield call(sessionResourceService.storeUpdatedSessionResource, session, { meetingMembers: updatedMembers });
  }

  return response.sessionId;
}

export function* addUserToMeetingMembers(userId: IUser['id'], meetingMembers: IUser['id'][]) {
  const updatedMeetingMembers = meetingMembers.slice();
  if (!updatedMeetingMembers.includes(userId)) {
    updatedMeetingMembers.push(String(userId));
  }
  return updatedMeetingMembers;
}

// export function* populateTasks(meetingId: IMeeting['id']) {
//   let meeting = (yield selectWithParams(meetingSelectors.selectResourceById, meetingId)) as IMeeting;

//   const taskIds: ITask['id'][] = yield call(taskService.searchTasks, { id: meetingId }); // ALO

//   if (!taskIds) {
//     return;
//   }
//   meeting = yield call(storeMeeting, { ...meeting, taskIds });

//   return meeting;
// }

// export function* setTasks(meetingId: IMeeting['id'], taskIds: ITask['id'][]) {
//   const meeting = (yield selectWithParams(meetingSelectors.selectResourceById, meetingId)) as IMeeting;

//   if (!meeting) {
//     return;
//   }
//   yield call(storeMeeting, { ...meeting, taskIds });

//   return meeting;
// }
export function* populateKpi(meetingId: IMeetingSimple['id']) {
  let meeting = (yield selectWithParams(meetingSimpleSelectors.selectResourceById, meetingId)) as IMeetingSimple;

  if (!meeting.kpiId) {
    const { scores, sessions, scoreboards } = (yield call(apiRequest, scoreApi.getScoreBoardApi({ meetingId }))) as {
      scores: IScore[];
      sessions: ISession[];
      scoreboards: {
        [sessionId: string]: IKpiRowScoreboard[];
      };
    };

    //meeting.sessionIds = sessions.map((session) => session.id);
    yield call(scoreService.storeScore, scores);
    yield call(sessionService.storeSession, sessions);

    const kpiRowScoreboards: IKpiRowScoreboard[] = [];
    const kpiRows: IKpiRow[] = [];

    const kpi = {
      id: meetingId,
      meetingId,
      sessionIds: sessions.map((session) => session.id),
      kpiRowIds: scores.map((score) => {
        const kpiRow = {
          id: score.id,
          scoreId: score.id,
          userId: score.userId,
          kpiRowScoreboardIds: sessions.map((session) => {
            let scoreboard = scoreboards[session.id].find(
              (scoreboard) => scoreboard.sessionId === session.id && scoreboard.scoresId === score.id,
            );
            if (!scoreboard) {
              scoreboard = {
                id: `${score.id}/${session.id}`,
                sessionId: session.id,
                scoresId: score.id,
                measured: '',
                note: '',
              };
            }
            kpiRowScoreboards.push(scoreboard);
            return scoreboard.id;
          }),
        };
        kpiRows.push(kpiRow);
        return kpiRow.id;
      }),
    };

    yield call(kpiRowScoreboardService.storeKpiRowScoreboard, kpiRowScoreboards);
    yield call(kpiRowService.storeKpiRow, kpiRows);

    yield call(kpiService.storeKpi, kpi);
    meeting = yield call(storeMeetingSimple, { ...meeting, kpiId: kpi.id });
  }

  return meeting;
}

export function* getKpis(meetingId: IMeetingSimple['id'], queryParams?: IKpiSearchParams) {
  let meeting = (yield selectWithParams(meetingSimpleSelectors.selectResourceById, meetingId)) as IMeetingSimple;

  const { scores, sessions, scoreboards } = (yield call(apiRequest, kpiApi.getKpisApi(meetingId, queryParams))) as {
    scores: IScore[];
    sessions: ISession[];
    scoreboards: {
      [sessionId: string]: IKpiRowScoreboard[];
    };
  };

  yield call(scoreService.storeScore, scores);
  yield call(sessionService.storeSession, sessions);

  const kpiRowScoreboards: IKpiRowScoreboard[] = [];
  const kpiRows: IKpiRow[] = [];

  const kpi = {
    id: meetingId,
    meetingId,
    sessionIds: sessions.map((session) => session.id),
    kpiRowIds: scores.map((score) => {
      const kpiRow = {
        id: score.id,
        scoreId: score.id,
        userId: score.userId,
        kpiRowScoreboardIds: sessions.map((session) => {
          let scoreboard = scoreboards[session.id]?.find(
            (scoreboard) => scoreboard.sessionId === session.id && scoreboard.scoresId === score.id,
          );
          if (!scoreboard) {
            scoreboard = {
              id: `${score.id}/${session.id}`,
              sessionId: session.id,
              scoresId: score.id,
              measured: '',
              note: '',
            };
          }
          kpiRowScoreboards.push(scoreboard);
          return scoreboard.id;
        }),
      };
      kpiRows.push(kpiRow);
      return kpiRow.id;
    }),
  };

  yield all([
    call(kpiRowScoreboardService.storeKpiRowScoreboard, kpiRowScoreboards),
    call(kpiRowService.storeKpiRow, kpiRows),
    call(kpiService.storeKpi, kpi),
  ]);
  meeting = yield call(storeMeetingSimple, { ...meeting, kpiId: kpi.id });

  return meeting;
}

// export function* setKpi(meetingId: IMeeting['id'], kpiId: IKpi['id']) {
//   const meeting = (yield selectWithParams(meetingSelectors.selectResourceById, meetingId)) as IMeeting;

//   if (!meeting) {
//     return;
//   }
//   yield call(storeMeeting, { ...meeting, kpiId }); // todo

//   return meeting;
// }

// export function* populateAgendaItems(meetingId: IMeeting['id']) {
//   let meeting = (yield selectWithParams(meetingSelectors.selectResourceById, meetingId)) as IMeeting;

//   if (!meeting.agendaItemIds) {
//     const agendaItems = (yield call(agendaService.searchAgendaItems, { id: meetingId })) as IAgendaItem[];
//     meeting = yield call(storeMeeting, { ...meeting, agendaItemIds: agendaItems.map((agenda) => agenda.id) });
//   }

//   return meeting;
// }

// export function* setAgendaItems(meetingId: IMeeting['id'], agendaItemIds: IAgendaItem['id'][]) {
//   const meeting = (yield selectWithParams(meetingSelectors.selectResourceById, meetingId)) as IMeeting;

//   if (!meeting) {
//     return;
//   }
//   yield call(storeMeeting, { ...meeting, agendaItemIds });

//   return meeting;
// }

export const meetingResponseToMeeting = (meetingResponse: IMeetingSimpleResponse): IMeetingSimple => ({
  id: meetingResponse.id,
  title: meetingResponse.title,
  description: meetingResponse.description,
  startDate: meetingResponse.startDate,
  endDate: meetingResponse.endDate,
  startTime: meetingResponse.startTime,
  duration: meetingResponse.duration,
  createdBy: meetingResponse.createdBy,
  googleDriveId: meetingResponse.googleDriveId,
  calendarSocialId: meetingResponse.calendarSocialId,
  calendarEventId: meetingResponse.calendarEventId,
  location: meetingResponse.location,
  sharepointUrl: meetingResponse.sharepointUrl,
  facebookGroupUrl: meetingResponse.facebookGroupUrl,
  fileDriveUrl: meetingResponse.fileDriveUrl,
  labels: meetingResponse.labels,
  shareWithComment: meetingResponse.shareWithComment,
  deleted: meetingResponse.deleted,
  color: meetingResponse.color,
  recurring: meetingResponse.recurring,
  repeatCheckbox: meetingResponse.repeatCheckbox,
  repeatEvery: meetingResponse.repeatEvery,
  repeatType: meetingResponse.repeatType,
  noOfCycles: meetingResponse.noOfCycles,
  lastStartDate: meetingResponse.lastStartDate,
  recurringDates: meetingResponse.recurringDates,
  confirmedBy: meetingResponse.confirmedBy,
  rejectedBy: meetingResponse.rejectedBy,
  image: meetingResponse.image,
  ytCode: meetingResponse.ytCode,
  type: meetingResponse.type,
  shareWithClientIds: meetingResponse.shareWithClients,
  administratorIds: meetingResponse.administrators,
  shareWithIds: meetingResponse.shareWith,
  clientId: meetingResponse.clientId,
  nextSession: meetingResponse.nextSession,
  nextSessionId: meetingResponse.nextSessionId,
  lastSession: meetingResponse.lastSession,
  lastSessionId: meetingResponse.lastSessionId,
  tenantId: meetingResponse.tenantId,
  precapMessage: meetingResponse.precapMessage,
  enableRecording: meetingResponse.enableRecording,
  kpisGroupedByUser: meetingResponse.kpisGroupedByUser,
});

/**
 * Updates the duration of the meeting in the Store based on the duration of each agenda item
 * @param meetingId ID of meeting ot update
 * @param agendaItems Array of agenda items
 * @returns The meeting object
 */
export function* updateMeetingDurationFromAgenda(meetingId: IMeetingSimple['id'], agendaItems: IAgendaItem[]) {
  const meeting = (yield selectWithParams(meetingSimpleSelectors.selectResourceById, meetingId)) as IMeetingSimple;

  const totalDuration = agendaItems.reduce((durationAcc, agendaItem) => {
    return durationAcc + (agendaItem.checked == '1' ? parseInt(agendaItem.duration) : 0);
  }, 0);
  const updatedMeteing = { ...meeting, duration: String(totalDuration) };

  yield put(actions.storeMeetingSimple(updatedMeteing));
  return updatedMeteing;
}
